// PoliticaPrivacidadPage.tsx - Página de Política de Privacidad para Algorithmia
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import LogoAlgorithmia from '../../src/assets/LOGOAZUL-Ajpg.jpg';

const PoliticaPrivacidadPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO
  title="Política de Privacidad | Algorithmia - Desarrollo de Software en Córdoba"
  description="Política de privacidad de Algorithmia. Información sobre cómo recopilamos, usamos y protegemos tus datos personales de acuerdo con la legislación argentina."
  keywords="política privacidad, protección datos, datos personales, privacidad web"
  canonicalUrl="https://algorithmia.com.ar/politica-privacidad"  // ✅ corregido
  url="https://algorithmia.com.ar/politica-privacidad"           // ✅ corregido
  noIndex={true}                                                  // ✅ agregado
/>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        {/* Header */}
        <nav className="sticky top-0 z-40 py-3 bg-white/95 backdrop-blur-lg border-b border-gray-200 shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
            <motion.button
              whileHover={{ scale: 1.05, x: -5 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-gray-600 font-medium text-sm hover:bg-gray-100 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span>Volver al Inicio</span>
            </motion.button>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-lg flex items-center justify-center shadow-lg overflow-hidden">
                <img src={LogoAlgorithmia} alt="Algorithmia Logo" className="h-8 w-auto p-0.5" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-[#4ADE80] via-[#3BC571] to-[#2D4A7D] bg-clip-text text-transparent">
                Algorithmia
              </span>
            </div>
          </div>
        </nav>

        {/* Contenido */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="p-4 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-2xl shadow-lg">
                <ShieldCheckIcon className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Política de Privacidad</h1>
            </div>

            <p className="text-sm text-gray-500 mb-8 pb-4 border-b border-gray-100">
              Última actualización: {new Date().toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <div className="space-y-8">
              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">1. Responsable del Tratamiento</h2>
                <p className="text-gray-700 leading-relaxed">
                  Algorithmia, con domicilio en Córdoba, Argentina, es el responsable del tratamiento 
                  de los datos personales facilitados por los usuarios a través de este sitio web.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">2. Datos Recopilados</h2>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Podemos recopilar la siguiente información:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Datos de contacto: nombre, email, teléfono, empresa</li>
                  <li>Datos de navegación: dirección IP, tipo de navegador, páginas visitadas</li>
                  <li>Datos de comunicación: información que nos proporcionas en formularios</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">3. Finalidad del Tratamiento</h2>
                <p className="text-gray-700 leading-relaxed mb-2">Tus datos serán utilizados para:</p>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Gestionar consultas y solicitudes de información</li>
                  <li>Enviar comunicaciones comerciales (previa autorización)</li>
                  <li>Mejorar nuestros servicios y la experiencia de usuario</li>
                  <li>Cumplir con obligaciones legales</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">4. Base Legal</h2>
                <p className="text-gray-700 leading-relaxed">
                  El tratamiento de tus datos se realiza sobre la base de tu consentimiento, 
                  la ejecución de un contrato o el interés legítimo de Algorithmia, según corresponda.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">5. Conservación de Datos</h2>
                <p className="text-gray-700 leading-relaxed">
                  Conservaremos tus datos personales mientras sean necesarios para las finalidades 
                  indicadas o mientras no solicites su supresión, siempre dentro de los plazos legales.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">6. Destinatarios de Datos</h2>
                <p className="text-gray-700 leading-relaxed">
                  No compartimos tus datos personales con terceros, excepto por obligación legal 
                  o para la correcta prestación de servicios (ej. proveedores de hosting).
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">7. Derechos del Usuario</h2>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Tienes derecho a:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Acceder a tus datos personales</li>
                  <li>Rectificar datos inexactos</li>
                  <li>Solicitar la supresión de tus datos</li>
                  <li>Limitar u oponerte al tratamiento</li>
                  <li>Portar tus datos a otro responsable</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">8. Ejercicio de Derechos</h2>
                <p className="text-gray-700 leading-relaxed">
                  Puedes ejercer tus derechos enviando un email a{' '}
                  <a href="mailto:contacto@algorithmia.com.ar" className="text-[#4ADE80] font-semibold hover:underline">
                    contacto@algorithmia.com.ar
                  </a>{' '}
                  con el asunto "Protección de Datos".
                </p>
              </section>
            </div>

            <div className="mt-12 p-6 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-gray-700 text-center">
                ¿Tienes dudas sobre nuestra política de privacidad? Contáctanos en{' '}
                <a href="mailto:contacto@algorithmia.com.ar" className="text-[#4ADE80] font-semibold hover:underline">
                  contacto@algorithmia.com.ar
                </a>
              </p>
            </div>
          </motion.div>
        </div>

        <footer className="py-8 bg-white border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-gray-600 text-sm">
              © {new Date().getFullYear()} Algorithmia. Todos los derechos reservados.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PoliticaPrivacidadPage;